import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import type { ComponentProps } from 'react'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import OfferCardMapper from './OfferCardMapper'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import useOffer from 'hooks/Offers/useOffer'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import { useInView } from 'react-intersection-observer'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import useToggle from 'hooks/useToggle'
import {
  calculateHasBeenInView,
  calculateTrackingInView,
  handleSkipInView,
} from 'components/utils/OfferInView'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'

const Card = styled.div`
  display: grid;
  width: 80vw;
  height: 100%;
  flex-shrink: 0;

  // Sold out villas return null so need to be hidden
  &:empty {
    display: none;
  }

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }

  ${mediaQueryUp.desktop} {
    &.size-medium {
      width: ${rem(512)};
    }
  }

  /* Fix width for hidden offer cards to prevent overlap on tablet and larger screens */
  &:has(.lux-plus-hidden-offer-card) {
    ${mediaQueryUp.tablet} {
      width: ${rem(512)};

      &.size-small {
        width: ${rem(320)};
      }
    }
  }
`

const LoadingSkeleton = styled(OfferCardLoadingSkeleton)`
  z-index: 0;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
  }
`

type InheritedOfferCardMapperProps = Pick<ComponentProps<typeof OfferCardMapper>, 'variant' | 'size' | 'eagerLoadImage'>

interface Props extends InheritedOfferCardMapperProps {
  id: string
  idx?: number
  className?: string
  lazyLoad?: boolean
  additionalOfferUrlQuery?: string
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function OfferCard({
  id,
  idx,
  lazyLoad,
  additionalOfferUrlQuery,
  variant,
  size,
  selectedDatesAndRoomsFilter,
  eagerLoadImage,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [skipInView, setSkipInView] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const [tileInViewRef, inView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, inView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  const canFetchOffer = !lazyLoad || hasBeenInView

  const [offer, , offerError] = useOffer(id, {
    requireSummaryOnly: true,
    disabled: !canFetchOffer,
  })

  const [loader,,, hideLoader] = useToggle(!offer)

  useEffect(() => {
    if (offer && trackingInView && imageLoaded && idx !== undefined) {
      dispatchOfferListEvent({
        offer,
        position: idx,
        type: OfferListEvents.productImpression,
        key: EventDataKey.ProductImpression,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView, offer, imageLoaded])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.offerReady) {
      setImageLoaded(true)
    }
    // Now that we've handled our case, propagate the event up to other listeners
    dispatchOfferListEvent(dispatchAction, additionalData)
  }, [dispatchOfferListEvent])

  if (offerError) {
    return null
  }

  const pauseLoader = !hasBeenInView || !!offer

  return (
    <ErrorBoundary fallback={null}>
      <LoaderPlayStateContext.Provider value={pauseLoader ? 'paused' : 'running'}>
        <Card ref={tileInViewRef} className={`size-${size}`} data-testid="offer-card">

          {!!offer && <OfferListEventsProvider onListEvent={onListEvent}>
            <OfferCardMapper
              offer={offer}
              size={size}
              variant={variant}
              idx={idx}
              additionalOfferUrlParams={additionalOfferUrlQuery}
              selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
              eagerLoadImage={eagerLoadImage}
            />
          </OfferListEventsProvider>}
          {loader && <LoadingSkeleton
            variant={variant}
            onTransitionEnd={hideLoader}
            className={!offer ? 'visible' : undefined}
            size={size}
          />}
        </Card>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
}

export default memo(OfferCard)
